import HlsPlus, {HlsPlusConfig, HlsPlusEvents, TrackMetadata} from './hls-plus';
import {Icons, Images} from './assets';

const PLAYER_WAKE_CHECK_INTERVAL_MS = 15_000;
const PLAYER_WAKE_RESET_THRESHOLD_MS = 45_000;
const RESUME_ON_TAB_STORAGE_KEY = 'shf_hlsplus_player_resume_on_tab';

const isResumeOnTabActiveEnabled = () => localStorage.getItem(RESUME_ON_TAB_STORAGE_KEY) === 'true';

const setResumeOnTabActiveEnabled = (enabled: boolean) => {
    localStorage.setItem(RESUME_ON_TAB_STORAGE_KEY, enabled.toString());
};

const setAttributes = (elem: HTMLElement, attrs: { [key: string]: string }) => {
    Object.entries(attrs).forEach(([name, value]) => {
        elem.setAttribute(name, value);
    });
};

const getGlobalStylesElem = (() => {
    let memo: HTMLStyleElement;
    return () => {
        if (memo) {
            return memo;
        }
        const headElem = document.head || document.getElementsByTagName('head')[0];
        const styleElem = document.createElement('style');
        headElem.appendChild(styleElem);
        memo = styleElem;
        return memo;
    };
})();

const setGlobalStyles = (css: string) => {
    const styleElem = getGlobalStylesElem();
    styleElem.appendChild(document.createTextNode(css));
};

const setStyles = (elem: HTMLElement, styles: Partial<CSSStyleDeclaration>) => {
    Object.assign(elem.style, styles);
};

const secondsToTime = (timeInSeconds: number): string => {
    const date = new Date(0);
    date.setSeconds(timeInSeconds);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    const secondsString = seconds < 10 ? `0${seconds}` : seconds.toString();
    let timeString = `${minutes}:${secondsString}`;
    if (hours > 0) {
        if (minutes < 10) {
            timeString = `0${timeString}`;
        }
        timeString = `${hours}:${timeString}`;
    }
    return timeString;
};

const getArtworkDisplayUrl = (imageUrl?: string, imageSize: number = 640): string => {
    if (!imageUrl) {
        return '';
    }
    try {
        const url = new URL(imageUrl, window.location.href);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
            url.searchParams.set('w', imageSize.toString());
            url.searchParams.set('upscale', 'true');
            url.searchParams.set('auto', 'compress,format');
        }
        return url.toString();
    } catch {
        return imageUrl;
    }
};

type BackgroundImageController = {
    getUrl: (imageUrl: string) => Promise<string>;
    onChange: (url: string) => void;
};

const loadingScreen = () => {
    setGlobalStyles(`
        .SHF_HLSPLUS_PLAYER_loading-spinner {
            position: relative;
            width: 80px;
            height: 80px;
            margin: auto;
        }
        .SHF_HLSPLUS_PLAYER_loading-spinner div {
            box-sizing: border-box;
            display: block;
            position: absolute;
            width: 64px;
            height: 64px;
            margin: 8px;
            border: 6px solid #F68A14;
            border-radius: 50%;
            animation: SHF_HLSPLUS_PLAYER_loading-spinner 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
            border-color: #F68A14 transparent transparent transparent;
        }
        .SHF_HLSPLUS_PLAYER_loading-spinner div:nth-child(1) {
            animation-delay: -0.25s;
        }
        .SHF_HLSPLUS_PLAYER_loading-spinner div:nth-child(2) {
            animation-delay: -0.15s;
        }
        .SHF_HLSPLUS_PLAYER_loading-spinner div:nth-child(3) {
            animation-delay: -0.05s;
        }
        @keyframes SHF_HLSPLUS_PLAYER_loading-spinner {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    `);
    const container = document.createElement('div');
    container.innerHTML = '<div class="SHF_HLSPLUS_PLAYER_loading-spinner"><div></div><div></div><div></div><div></div></div>';
    setStyles(container, {
        display: 'flex',
        position: 'absolute',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
        backgroundColor: 'rgba(0,0,0,0.2)',
        pointerEvents: 'none',
        zIndex: '2',
    });
    return container;
};

const vidPlayer = () => {
    const vidElem = document.createElement('video');
    setAttributes(vidElem, {
        playsinline: '',
        preload: 'auto',
        height: '10px',
    });
    setStyles(vidElem, {
        display: 'none',
    });
    return vidElem;
};

const albumArtwork = (
    hlsPlus: HlsPlus,
    fallbackAlbumArtUrl?: string,
    backgroundImage?: BackgroundImageController,
) => {
    const container = document.createElement('div');
    const artworkContainer = document.createElement('div');
    let activeImageElem = document.createElement('img');
    let nextImageElem = document.createElement('img');
    const textRows = document.createElement('div');
    const titleElem = document.createElement('div');
    const artistElem = document.createElement('div');
    const loadingElem = loadingScreen();
    const albumElem = document.createElement('div');
    titleElem.textContent = '\u00a0';
    artistElem.textContent = '\u00a0';
    albumElem.textContent = '\u00a0';
    const fallbackArtworkUrl = fallbackAlbumArtUrl || Images.defaultAlbumArt;
    activeImageElem.src = fallbackArtworkUrl;
    activeImageElem.style.opacity = '1';
    nextImageElem.style.opacity = '0';
    loadingElem.style.display = 'none';
    let pendingArtworkLoadId = 0;
    let prevBackgroundImageUrl: string | undefined;

    const loadBackgroundImage = async (imageUrl: string): Promise<string | undefined> => {
        if (!backgroundImage) {
            return undefined;
        }
        return backgroundImage.getUrl(imageUrl);
    };

    const applyLoadedBackgroundImage = (newImageUrl?: string) => {
        if (!backgroundImage || !newImageUrl) {
            return;
        }
        backgroundImage.onChange(newImageUrl);
        if (prevBackgroundImageUrl) {
            URL.revokeObjectURL(prevBackgroundImageUrl);
        }
        prevBackgroundImageUrl = newImageUrl;
    };

    const styleArtworkImage = (imageElement: HTMLImageElement) => {
        setStyles(imageElement, {
            position: 'absolute',
            display: 'block',
            width: '100%',
            height: '100%',
            opacity: imageElement.style.opacity || '0',
            transition: 'opacity 0.2s',
            zIndex: '1',
        });
    };

    const showArtwork = (newActiveImageElem: HTMLImageElement) => {
        activeImageElem.style.opacity = '0';
        newActiveImageElem.style.opacity = '1';
        if (newActiveImageElem !== activeImageElem) {
            const oldImageElem = activeImageElem;
            activeImageElem = newActiveImageElem;
            nextImageElem = oldImageElem;
        }
    };

    const updateMediaSession = (data: TrackMetadata, artworkUrl?: string, imageSize: number = 640) => {
        if (!('mediaSession' in navigator)) {
            return;
        }
        const meta: any = {
            title: data.title,
            artist: data.artist,
            album: data.album,
        };
        if (artworkUrl) {
            meta.artwork = [
                {
                    src: artworkUrl,
                    sizes: `${imageSize}x${imageSize}`,
                    type: 'image/jpg',
                },
            ];
        }
        try {
            navigator.mediaSession.metadata = new MediaMetadata(meta);
        } catch (e) {
            // Defensive: some platforms require artwork.src to be defined.
            console.warn('Failed to set mediaSession.metadata', e);
        }
    };

    artworkContainer.className = 'SHF_HLSPLUS_PLAYER_artwork';
    textRows.className = 'SHF_HLSPLUS_PLAYER_track_text';
    setGlobalStyles(`
        .SHF_HLSPLUS_PLAYER_artwork {
            width: min(100%, max(220px, calc(100vh - 245px)));
        }
        @media (height <= 540px) {
            .SHF_HLSPLUS_PLAYER_root {
                padding: 14px !important;
            }
            .SHF_HLSPLUS_PLAYER_track_text {
                line-height: 1.4 !important;
                padding: 8px !important;
            }
        }
    `);

    setStyles(artworkContainer, {
        position: 'relative',
        aspectRatio: '1 / 1',
        maxWidth: '100%',
        margin: '0 auto',
        border: 'white solid 1px',
        userSelect: 'none',
        // @ts-ignore
        '-webkit-user-select': 'none',
    });
    styleArtworkImage(activeImageElem);
    styleArtworkImage(nextImageElem);
    setStyles(textRows, {
        textAlign: 'center',
        lineHeight: '1.6',
        padding: '10px',
        opacity: '1',
        transition: 'opacity 0.2s',
    });
    setStyles(titleElem, {
        fontWeight: 'bold',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    });
    setStyles(albumElem, {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    });
    setStyles(artistElem, {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    });

    hlsPlus.on(HlsPlusEvents.TRACK_CHANGED, (data: TrackMetadata) => {
        let imageSize = 640;
        let imageUrl = getArtworkDisplayUrl(data.image, imageSize);

        const displayUrl = imageUrl || fallbackAlbumArtUrl || Images.defaultAlbumArt;
        updateMediaSession(data);

        if (!imageUrl) {
            const artworkLoadId = ++pendingArtworkLoadId;
            loadingElem.style.display = 'none';
            if (activeImageElem.src !== displayUrl) {
                nextImageElem.setAttribute('src', displayUrl);
                showArtwork(nextImageElem);
            }
            updateMediaSession(data, displayUrl, imageSize);
            const backgroundReady = loadBackgroundImage('')
                .catch((error) => {
                    console.error('Error processing fallback background image', error);
                    return undefined;
                });
            backgroundReady.then((newBackgroundImageUrl) => {
                if (artworkLoadId !== pendingArtworkLoadId) {
                    if (newBackgroundImageUrl) {
                        URL.revokeObjectURL(newBackgroundImageUrl);
                    }
                    return;
                }
                applyLoadedBackgroundImage(newBackgroundImageUrl);
            });
        } else {
            // Keep the previous cover visible until the new remote artwork is actually ready.
            if (activeImageElem.src !== displayUrl) {
                const artworkLoadId = ++pendingArtworkLoadId;
                const imageToLoad = nextImageElem;
                const fallback = fallbackArtworkUrl;
                const backgroundReady = loadBackgroundImage(imageUrl)
                    .catch((error) => {
                        console.error('Error processing background image', error);
                        return undefined;
                    });
                loadingElem.style.display = 'flex';
                imageToLoad.onload = () => {
                    if (artworkLoadId !== pendingArtworkLoadId) {
                        return;
                    }
                    loadingElem.style.display = 'none';
                    showArtwork(imageToLoad);
                    updateMediaSession(data, displayUrl, imageSize);
                    backgroundReady.then((newBackgroundImageUrl) => {
                        if (artworkLoadId !== pendingArtworkLoadId) {
                            if (newBackgroundImageUrl) {
                                URL.revokeObjectURL(newBackgroundImageUrl);
                            }
                            return;
                        }
                        applyLoadedBackgroundImage(newBackgroundImageUrl);
                    });
                };
                imageToLoad.onerror = () => {
                    if (artworkLoadId !== pendingArtworkLoadId) {
                        return;
                    }
                    loadingElem.style.display = 'none';
                    if (activeImageElem.src !== fallback) {
                        imageToLoad.setAttribute('src', fallback);
                    } else {
                        showArtwork(activeImageElem);
                    }
                    updateMediaSession(data, fallback, imageSize);
                };
                imageToLoad.setAttribute('src', displayUrl);
            }
        }

        textRows.style.opacity = '0';
        setTimeout(() => {
            titleElem.textContent = data.title;
            artistElem.textContent = data.artist;
            albumElem.textContent = data.album;
            textRows.style.opacity = '1';
        }, 200);
    });

    artworkContainer.appendChild(loadingElem);
    artworkContainer.appendChild(activeImageElem);
    artworkContainer.appendChild(nextImageElem);
    container.appendChild(artworkContainer);
    textRows.appendChild(titleElem);
    textRows.appendChild(artistElem);
    textRows.appendChild(albumElem);
    container.appendChild(textRows);
    return container;
};

const installResumeOnTabMenu = (target: HTMLElement) => {
    setGlobalStyles(`
        .SHF_HLSPLUS_PLAYER_context_menu {
            position: fixed;
            z-index: 2147483646;
            min-width: 240px;
            padding: 4px;
            background: rgba(18, 18, 18, 0.96);
            border: 1px solid rgba(255, 255, 255, 0.18);
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
            font-family: -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif;
            font-size: 14px;
            line-height: 1.3;
            color: #FFFFFF;
        }
        .SHF_HLSPLUS_PLAYER_context_menu button {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            margin: 0;
            padding: 8px 10px;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: inherit;
            font: inherit;
            text-align: left;
            cursor: pointer;
        }
        .SHF_HLSPLUS_PLAYER_context_menu button:hover,
        .SHF_HLSPLUS_PLAYER_context_menu button:focus-visible {
            background: rgba(255, 255, 255, 0.12);
            outline: none;
        }
        .SHF_HLSPLUS_PLAYER_context_menu_check {
            width: 14px;
            flex: 0 0 14px;
        }
    `);

    let menuElem: HTMLDivElement | null = null;

    const closeMenu = () => {
        if (!menuElem) {
            return;
        }
        menuElem.remove();
        menuElem = null;
        document.removeEventListener('pointerdown', onDocumentPointerDown, true);
        document.removeEventListener('keydown', onDocumentKeyDown, true);
        window.removeEventListener('resize', closeMenu);
        window.removeEventListener('scroll', closeMenu, true);
    };

    const onDocumentPointerDown = (event: Event) => {
        if (menuElem && event.target instanceof Node && menuElem.contains(event.target)) {
            return;
        }
        closeMenu();
    };

    const onDocumentKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    };

    const openMenu = (clientX: number, clientY: number) => {
        closeMenu();

        const enabled = isResumeOnTabActiveEnabled();
        menuElem = document.createElement('div');
        menuElem.className = 'SHF_HLSPLUS_PLAYER_context_menu';
        menuElem.setAttribute('role', 'menu');

        const item = document.createElement('button');
        item.type = 'button';
        item.setAttribute('role', 'menuitemcheckbox');
        item.setAttribute('aria-checked', enabled.toString());
        const check = document.createElement('span');
        check.className = 'SHF_HLSPLUS_PLAYER_context_menu_check';
        check.textContent = enabled ? '✓' : '';
        const label = document.createElement('span');
        label.textContent = 'Resume playback when tab is active';
        item.appendChild(check);
        item.appendChild(label);
        item.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            setResumeOnTabActiveEnabled(!isResumeOnTabActiveEnabled());
            closeMenu();
        });
        menuElem.appendChild(item);
        document.body.appendChild(menuElem);

        const rect = menuElem.getBoundingClientRect();
        const x = Math.max(8, Math.min(clientX, window.innerWidth - rect.width - 8));
        const y = Math.max(8, Math.min(clientY, window.innerHeight - rect.height - 8));
        menuElem.style.left = `${x}px`;
        menuElem.style.top = `${y}px`;

        document.addEventListener('pointerdown', onDocumentPointerDown, true);
        document.addEventListener('keydown', onDocumentKeyDown, true);
        window.addEventListener('resize', closeMenu);
        window.addEventListener('scroll', closeMenu, true);
    };

    target.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openMenu(event.clientX, event.clientY);
    });
};

const timeline = (hlsPlus: HlsPlus, mediaPlayer: HTMLMediaElement) => {
    const container = document.createElement('div');
    const seekBarContainer = document.createElement('div');
    const seekBar = document.createElement('div');
    const elapsedBar = document.createElement('div');
    const timeRow = document.createElement('div');
    const elapsedTimeDisplay = document.createElement('div');
    const durationDisplay = document.createElement('div');
    let timeOffset = mediaPlayer.currentTime;
    let trackDuration = 300;
    elapsedTimeDisplay.textContent = '-:--';
    durationDisplay.textContent = '-:--';
    setStyles(container, {
        userSelect: 'none',
        // @ts-ignore
        '-webkit-user-select': 'none',
    });
    setStyles(seekBarContainer, {
        position: 'relative',
        width: '100%',
        height: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    });
    setStyles(seekBar, {
        position: 'relative',
        width: '100%',
        height: '2px',
        backgroundColor: 'rgba(255,255,255, 0.2)',
    });
    setStyles(elapsedBar, {
        position: 'absolute',
        height: '100%',
        backgroundColor: '#FFFFFF',
        width: '0%',
    });
    setStyles(timeRow, {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
    });

    // When the server provides an elapsed time for the current segment
    // we'll record the reported elapsed seconds and the timestamp when
    // it was received. Prefer using this authoritative value when
    // rendering the playbar instead of deriving an offset from the
    // player's currentTime (which produced incorrect values).
    let serverElapsedSec: number | null = null;
    let serverElapsedAtMs: number | null = null;
    let serverElapsedTrackId: string | null = null;

    const clearServerElapsed = () => {
        serverElapsedSec = null;
        serverElapsedAtMs = null;
        serverElapsedTrackId = null;
    };

    const renderElapsed = () => {
        let elapsedTime: number;
        if (serverElapsedSec !== null && serverElapsedAtMs !== null) {
            const deltaSec = (Date.now() - serverElapsedAtMs) / 1000;
            elapsedTime = serverElapsedSec + deltaSec;
        } else {
            elapsedTime = mediaPlayer.currentTime - timeOffset;
        }
        elapsedTime = Math.min(Math.max(elapsedTime, 0), trackDuration);
        elapsedBar.style.width = trackDuration ? `${(elapsedTime / trackDuration) * 100}%` : '0%';
        elapsedTimeDisplay.textContent = secondsToTime(elapsedTime);
    };

    hlsPlus.on(HlsPlusEvents.TRACK_CHANGED, (data: TrackMetadata) => {
        // When a new track is selected, clear any server-provided elapsed
        // values so we don't continue to show stale elapsed times.
        if (serverElapsedTrackId !== data.id) {
            clearServerElapsed();
        }
        // Default timeOffset behavior: if player is at start prefer 0.
        if (mediaPlayer.currentTime <= 0.5) {
            timeOffset = 0;
        } else {
            timeOffset = mediaPlayer.currentTime;
        }
        trackDuration = data.duration_ms / 1000;
        durationDisplay.textContent = secondsToTime(trackDuration);
        renderElapsed();
    });

    // If the stream provides segment-level elapsed time, use it to set
    // the playbar offset so the elapsed display reflects the server state.
    hlsPlus.on(HlsPlusEvents.SEGMENT_CHANGED, (segment: any) => {
        try {
            if (segment?.current?.duration_ms) {
                trackDuration = segment.current.duration_ms / 1000;
                durationDisplay.textContent = secondsToTime(trackDuration);
            }

            if (typeof segment?.current?.elapsed_ms === 'number') {
                // Record authoritative server elapsed seconds and timestamp.
                serverElapsedSec = Math.max(0, segment.current.elapsed_ms / 1000);
                serverElapsedAtMs = Date.now();
                serverElapsedTrackId = segment.current.id || null;
                renderElapsed();
            }
        } catch (e) {
            // Defensive: ignore malformed segment data
        }
    });

    hlsPlus.on(HlsPlusEvents.TRACK_METADATA_CHANGED, (data: TrackMetadata) => {
        trackDuration = data.duration_ms / 1000;
        durationDisplay.textContent = secondsToTime(trackDuration);
        renderElapsed();
    });

    mediaPlayer.addEventListener('timeupdate', () => {
        renderElapsed();
    });

    seekBarContainer.appendChild(seekBar);
    seekBar.appendChild(elapsedBar);
    container.appendChild(seekBarContainer);

    timeRow.appendChild(elapsedTimeDisplay);
    timeRow.appendChild(durationDisplay);
    container.appendChild(timeRow);
    return container;
};

const controls = (mediaPlayer: HTMLMediaElement, hlsPlus: HlsPlus) => {
    const container = document.createElement('div');
    const volContainer = volumeControl(mediaPlayer);
    const playPauseSkipContainer = document.createElement('div');
    const playPauseBtn = playPauseButton(mediaPlayer);
    const skipBtn = skipButton(hlsPlus);
    const hlsOrHlsPlusIconContainer = document.createElement('div');
    const hlsPlusIcon = document.createElement('img');

    setStyles(container, {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        userSelect: 'none',
        // @ts-ignore
        '-webkit-user-select': 'none',
    });
    setStyles(volContainer, {
        flexBasis: '33%',
    });
    setStyles(playPauseSkipContainer, {
        display: 'flex',
        flexBasis: '33%',
        justifyContent: 'center',
    });
    setStyles(hlsOrHlsPlusIconContainer, {
        display: 'flex',
        flexBasis: '33%',
        justifyContent: 'flex-end',
    });
    setAttributes(hlsPlusIcon, {
        'src': hlsPlus.isInteractive ? Icons.hlsPlus : Icons.hls,
        'alt': hlsPlus.isInteractive ? 'HLS+' : 'HLS',
    });
    setStyles(hlsPlusIcon, {
        height: '22px',
        borderRadius: '4px',
        cursor: 'context-menu',
    });

    playPauseSkipContainer.appendChild(playPauseBtn);
    if (hlsPlus.isInteractive) {
        playPauseSkipContainer.appendChild(skipBtn);
    }

    if (!hlsPlus.isDirectStream) {
        hlsOrHlsPlusIconContainer.appendChild(hlsPlusIcon);
        installResumeOnTabMenu(hlsPlusIcon);
    } else {
        installResumeOnTabMenu(playPauseBtn);
    }

    container.appendChild(volContainer);
    container.appendChild(playPauseSkipContainer);
    container.appendChild(hlsOrHlsPlusIconContainer);
    return container;
};

const cachedMuteStateKey = 'shf_hlsplus_player_mute';
const muteButton = (mediaPlayer: HTMLMediaElement) => {
    const container = document.createElement('div');
    const vol0Icon = document.createElement('img');
    vol0Icon.src = Icons.vol0;
    const vol1Icon = document.createElement('img');
    vol1Icon.src = Icons.vol1;
    const vol2Icon = document.createElement('img');
    vol2Icon.src = Icons.vol2;
    const vol3Icon = document.createElement('img');
    vol3Icon.src = Icons.vol3;

    vol0Icon.style.display = 'none';
    vol1Icon.style.display = 'none';
    vol2Icon.style.display = 'none';

    let initialMutedState = false;
    const cachedMuteState = localStorage.getItem(cachedMuteStateKey);
    if (cachedMuteState) {
        initialMutedState = cachedMuteState === 'true';
    }
    mediaPlayer.muted = initialMutedState;

    setStyles(container, {
        display: 'flex',
        cursor: 'pointer',
        width: '28px',
        minHeight: '30px',
    });

    mediaPlayer.addEventListener('volumechange', () => {
        const isMuted = mediaPlayer.muted || mediaPlayer.volume === 0;
        if (isMuted) {
            vol3Icon.style.display = 'none';
            vol2Icon.style.display = 'none';
            vol1Icon.style.display = 'none';
            vol0Icon.style.display = '';
        } else if (mediaPlayer.volume < 0.5) {
            vol3Icon.style.display = 'none';
            vol2Icon.style.display = 'none';
            vol1Icon.style.display = '';
            vol0Icon.style.display = 'none';
        } else if (mediaPlayer.volume < 1) {
            vol3Icon.style.display = 'none';
            vol2Icon.style.display = '';
            vol1Icon.style.display = 'none';
            vol0Icon.style.display = 'none';
        } else {
            vol3Icon.style.display = '';
            vol2Icon.style.display = 'none';
            vol1Icon.style.display = 'none';
            vol0Icon.style.display = 'none';
        }
    });
    container.addEventListener('click', () => {
        mediaPlayer.muted = !mediaPlayer.muted;
        localStorage.setItem(cachedMuteStateKey, mediaPlayer.muted.toString());
    });

    container.appendChild(vol3Icon);
    container.appendChild(vol2Icon);
    container.appendChild(vol1Icon);
    container.appendChild(vol0Icon);
    return container;
};

const volumeControl = (mediaPlayer: HTMLMediaElement) => {
    const container = document.createElement('div');
    const muteBtn = muteButton(mediaPlayer);
    const volumeBarParent = document.createElement('div');
    const volumeBarChild = document.createElement('div');
    const volumeBarChildHead = document.createElement('div');
    const volumeNumber = document.createElement('div');
    let initialVolState = 1;
    const cachedVolKey = 'shf_hlsplus_player_vol';
    const cachedVolState = localStorage.getItem(cachedVolKey);
    if (cachedVolState) {
        initialVolState = Math.max(Math.min(parseFloat(cachedVolState), 1), 0);
    }

    const volumeBarWidthMaxPx = 50;
    volumeNumber.textContent = `${initialVolState * 10}`;
    mediaPlayer.volume = initialVolState;

    const volControlsClass = 'SHF_HLSPLUS_PLAYER_vol_ctrls';
    const compactVolControlsClass = 'SHF_HLSPLUS_PLAYER_compact_vol_ctrls';
    muteBtn.className = volControlsClass;
    volumeBarParent.className = `${volControlsClass} ${compactVolControlsClass}`;
    volumeNumber.className = `${volControlsClass} ${compactVolControlsClass}`;

    setGlobalStyles(`
        @media (width <= 320px) {
            .${compactVolControlsClass} {
                display: none !important;
            }
        }
    `);

    setStyles(container, {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        cursor: 'pointer',
    });
    setStyles(volumeBarParent, {
        position: 'relative',
        background: `linear-gradient(
            to right,
            rgba(255,255,255,0),
            rgba(255,255,255,0) 4px,
            rgba(255,255,255,0.2) 4px,
            rgba(255,255,255,0.2)
        )`,
        borderLeft: '#FFFFFF solid 1px',
        backgroundSize: '5px 100%',
        width: `${volumeBarWidthMaxPx}px`,
        height: '12px',
    });
    setStyles(volumeBarChild, {
        position: 'relative',
        background: `linear-gradient(
            to right,
            rgba(255,255,255,0),
            rgba(255,255,255,0) 4px,
            rgba(255,255,255,1) 4px,
            rgba(255,255,255,1)
        )`,
        backgroundSize: '5px 100%',
        width: mediaPlayer.muted ? '0%' : `${mediaPlayer.volume * 100}%`,
        height: '12px',
        transition: 'width 0.06s ease-out',
    });
    setStyles(volumeBarChildHead, {
        position: 'absolute',
        top: '-4px',
        right: '0px',
        width: '1px',
        height: '20px',
        backgroundColor: '#FFFFFF',
    });
    setStyles(volumeNumber, {
        fontSize: '10px',
        textAlign: 'center',
        width: '22px',
        height: '30px',
        lineHeight: '30px',
    });

    mediaPlayer.addEventListener('volumechange', () => {
        if (mediaPlayer.muted) {
            volumeBarChild.style.width = '0%';
        } else {
            volumeBarChild.style.width = `${mediaPlayer.volume * 100}%`;
        }
        volumeNumber.textContent = mediaPlayer.muted ? '0' : `${Math.round(mediaPlayer.volume * 10)}`;
        if (!mediaPlayer.muted && mediaPlayer.volume > 0 && mediaPlayer.volume < 0.1) {
            volumeNumber.textContent = '1';
        }
    });

    let xOrigin = 0;

    const onMouseDown = (event: MouseEvent) => {
        mediaPlayer.muted = false;
        mediaPlayer.volume = Math.max(Math.min(event.offsetX / volumeBarWidthMaxPx, 1), 0);
        xOrigin = event.pageX - event.offsetX;
        document.body.addEventListener('mouseup', onMouseUp);
        document.body.addEventListener('mouseleave', onMouseLeave);
        document.body.addEventListener('mousemove', onMouseMove);
    };

    const stopDragging = () => {
        document.body.removeEventListener('mouseup', onMouseUp);
        document.body.removeEventListener('mouseleave', onMouseLeave);
        document.body.removeEventListener('mousemove', onMouseMove);
        localStorage.setItem(cachedVolKey, mediaPlayer.volume.toString());
        localStorage.setItem(cachedMuteStateKey, mediaPlayer.muted.toString());
    };

    const onMouseUp = (event: MouseEvent) => {
        stopDragging();
    };

    const onMouseLeave = (event: MouseEvent) => {
        stopDragging();
    };

    const onMouseMove = (event: MouseEvent) => {
        mediaPlayer.volume = Math.max(Math.min((event.pageX - xOrigin) / volumeBarWidthMaxPx, 1), 0);
    };

    volumeBarParent.addEventListener('mousedown', onMouseDown);
    volumeNumber.addEventListener('click', () => {
        mediaPlayer.volume = 1;
        mediaPlayer.muted = false;
        localStorage.setItem(cachedVolKey, mediaPlayer.volume.toString());
        localStorage.setItem(cachedMuteStateKey, mediaPlayer.muted.toString());
    });

    container.appendChild(muteBtn);
    volumeBarChild.appendChild(volumeBarChildHead);
    volumeBarParent.appendChild(volumeBarChild);
    container.appendChild(volumeBarParent);
    container.appendChild(volumeNumber);
    return container;
};

const skipButton = (hlsPlus: HlsPlus) => {
    const container = document.createElement('div');
    const skipButton = document.createElement('div');
    setStyles(skipButton, {
        cursor: 'pointer',
        position: 'relative',
        opacity: hlsPlus.canSkip ? '1' : '0.4',
        transition: 'opacity 0.2s',
    });
    const skipIcon = document.createElement('img');
    skipIcon.src = Icons.skip;
    skipButton.appendChild(skipIcon);
    const availableSkipsCount = document.createElement('div');
    setStyles(availableSkipsCount, {
        position: 'absolute',
        top: '6px',
        right: '6px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '20px',
        width: '20px',
        textAlign: 'center',
        borderRadius: '10px',
        backgroundColor: '#F68A14',
        color: '#000000',
        fontWeight: 'bold',
        fontSize: '10px',
    });
    availableSkipsCount.textContent = '0';
    skipButton.appendChild(availableSkipsCount);
    hlsPlus.on(HlsPlusEvents.AVAILABLE_SKIPS_CHANGED, (data) => {
        availableSkipsCount.textContent = data.availableSkips;
        if (data.availableSkips > 0) {
            skipButton.style.opacity = '1';
        } else {
            skipButton.style.opacity = '0.4';
        }
    });
    hlsPlus.on(HlsPlusEvents.TRACK_LOADING, () => {
        skipButton.style.opacity = '0.4';
    });
    hlsPlus.on(HlsPlusEvents.TRACK_LOADED, () => {
        if (hlsPlus.canSkip) {
            skipButton.style.opacity = '1';
        }
    });
    skipButton.addEventListener('click', () => {
        hlsPlus.skip();
    });
    container.appendChild(skipButton);
    return container;
};

const playPauseButton = (mediaPlayer: HTMLMediaElement) => {
    const container = document.createElement('div');
    const playPauseButton = document.createElement('div');
    const playIcon = document.createElement('img');
    playPauseButton.setAttribute('data-testid', 'shf-play-btn');
    playPauseButton.style.cursor = 'pointer';
    playIcon.src = Icons.play;
    const pauseIcon = document.createElement('img');
    pauseIcon.src = Icons.pause;
    playPauseButton.appendChild(playIcon);
    playPauseButton.appendChild(pauseIcon);
    pauseIcon.style.display = 'none';

    mediaPlayer.addEventListener('play', () => {
        playIcon.style.display = 'none';
        pauseIcon.style.display = '';
    });
    mediaPlayer.addEventListener('pause', () => {
        playIcon.style.display = '';
        pauseIcon.style.display = 'none';
    });
    playPauseButton.addEventListener('click', () => {
        if (mediaPlayer.paused) {
            mediaPlayer.play().catch((e) => {
                console.error(`Unable to autoplay prior to user interaction with the dom. ${e}`);
            });
        } else {
            mediaPlayer.pause();
        }
    });
    container.appendChild(playPauseButton);
    return container;
};

const initBackgroundImage = async (
    fallbackAlbumArtUrl?: string,
): Promise<(imageUrl: string) => Promise<string>> => {
    return new Promise((resolve, reject) => {
        if (!window.Worker) {
            reject(new Error('background image generation not supported'))
            return;
        }
        try {
            const bgGeneratorWorker = new Worker('./bg-generator-worker.js', {type: 'module'});
            const pendingRequests = new Map<number, {resolve: (url: string) => void, reject: (error: Error) => void}>();
            let nextRequestId = 0;

            const getBgImage = async (imageUrl: string): Promise<string> => {
                return new Promise((resolve, reject) => {
                    try {
                        const requestId = ++nextRequestId;
                        pendingRequests.set(requestId, {resolve, reject});
                        bgGeneratorWorker.postMessage({
                            type: 'process',
                            requestId,
                            imageUrl,
                            fallbackImageUrl: fallbackAlbumArtUrl,
                        });
                    } catch (error) {
                        reject(error);
                    }
                })
            };

            bgGeneratorWorker.onmessage = event => {
                const data = event.data;
                if (data === 'ready') {
                    resolve(getBgImage);
                    return;
                }
                const requestId = data?.requestId;
                const pendingRequest = pendingRequests.get(requestId);
                if (!pendingRequest) {
                    return;
                }
                pendingRequests.delete(requestId);
                if (data?.type === 'failed') {
                    pendingRequest.reject(new Error('bg-generator-worker failed'));
                    return;
                }
                if (data?.type === 'processed' && data.imageUrl) {
                    pendingRequest.resolve(data.imageUrl);
                }
            };

            bgGeneratorWorker.postMessage({type: 'init'});
        } catch (error) {
            reject(error);
        }
    });
};

const installWakeRefresh = (hlsPlus: HlsPlus, mediaPlayer: HTMLMediaElement) => {
    let lastWakeCheckMs = Date.now();
    let refreshInFlight = false;
    let intendedPlaying = false;

    mediaPlayer.addEventListener('play', () => {
        intendedPlaying = true;
    });
    mediaPlayer.addEventListener('pause', () => {
        window.setTimeout(() => {
            if (document.visibilityState === 'visible' && mediaPlayer.paused) {
                intendedPlaying = false;
            }
        }, 50);
    });

    const refresh = async (reset: boolean, resume = false) => {
        if (refreshInFlight) {
            return;
        }

        refreshInFlight = true;
        try {
            await hlsPlus.refresh({reset, resume});
        } finally {
            lastWakeCheckMs = Date.now();
            refreshInFlight = false;
        }
    };

    const consumeWakeGapMs = () => {
        const nowMs = Date.now();
        const gapMs = nowMs - lastWakeCheckMs;
        lastWakeCheckMs = nowMs;
        return gapMs;
    };

    // Player is still playing — recover a stale live stream without pausing it.
    const recoverIfStillPlaying = () => {
        const gapMs = consumeWakeGapMs();
        if (mediaPlayer.paused || gapMs < PLAYER_WAKE_RESET_THRESHOLD_MS) {
            return;
        }
        if (document.visibilityState && document.visibilityState !== 'visible') {
            return;
        }
        void refresh(false);
    };

    const resumeAfterBrowserPause = () => {
        const gapMs = consumeWakeGapMs();
        if (gapMs >= PLAYER_WAKE_RESET_THRESHOLD_MS) {
            void refresh(true, true);
            return;
        }
        if (mediaPlayer.paused) {
            mediaPlayer.play().catch((error) => {
                console.error(`Unable to autoplay prior to user interaction with the dom. ${error}`);
            });
        }
    };

    window.setInterval(recoverIfStillPlaying, PLAYER_WAKE_CHECK_INTERVAL_MS);
    window.addEventListener('focus', recoverIfStillPlaying);
    window.addEventListener('pageshow', recoverIfStillPlaying);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            if (!mediaPlayer.paused) {
                recoverIfStillPlaying();
                return;
            }
            if (intendedPlaying && isResumeOnTabActiveEnabled()) {
                resumeAfterBrowserPause();
                return;
            }
            lastWakeCheckMs = Date.now();
            return;
        }
        lastWakeCheckMs = Date.now();
    });
};

const init = async (config: {
    id: string,
    streamUrl: string,
    fallbackAlbumArtUrl?: string,
    onBackgroundImageChange?: (url: string) => void,
    onInit?: () => void,
} & HlsPlusConfig) => {
    const container = document.getElementById(config.id);
    if (!container) {
        throw new Error('missing container');
    }
    if (HlsPlus.isSupported(config.streamUrl)) {
        const shfHlsPlusElem = document.createElement('div');
        shfHlsPlusElem.className = 'SHF_HLSPLUS_PLAYER_root';
        shfHlsPlusElem.setAttribute('data-testid', 'shf-player-container');
        setStyles(shfHlsPlusElem, {
            boxSizing: 'border-box',
            margin: '0',
            padding: '20px',
            width: '100%',
            position: 'relative',
            border: '0',
            fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif',
            fontSize: '16px',
            lineHeight: '1',
            color: '#FFFFFF',
            // @ts-ignore
            '-moz-osx-font-smoothing': 'grayscale',
            '-webkit-font-smoothing': 'antialiased',
        });

        const mediaPlayer = vidPlayer();
        const hlsPlus = new HlsPlus(config);
        await hlsPlus.loadSource(config.streamUrl);
        const backgroundImage = config.onBackgroundImageChange ? {
            getUrl: await initBackgroundImage(config.fallbackAlbumArtUrl),
            onChange: config.onBackgroundImageChange,
        } : undefined;

        shfHlsPlusElem.appendChild(mediaPlayer);
        shfHlsPlusElem.appendChild(albumArtwork(hlsPlus, config.fallbackAlbumArtUrl, backgroundImage));
        shfHlsPlusElem.appendChild(timeline(hlsPlus, mediaPlayer));
        shfHlsPlusElem.appendChild(controls(mediaPlayer, hlsPlus));

        await hlsPlus.attachMedia(mediaPlayer);
        installWakeRefresh(hlsPlus, mediaPlayer);
        container.appendChild(shfHlsPlusElem);
        if (config.onInit) {
            config.onInit();
        }
    } else {
        throw new Error('HLS+ is not supported');
    }
};

export default {init};
